<script setup lang="ts">
import BaseConfirmModal from "@/components/Base/ConfirmModal.vue";
import { useDialogQueue, type QueuedDialog } from "@/utils/confirmDialog";

defineOptions({ name: "ConfirmDialogHost" });

const { next, take, registerHost, unregisterHost } = useDialogQueue();

// One dialog at a time: the next one is only taken once Bootstrap has finished
// hiding the previous. Reusing the open modal instead would leave the second
// dialog invisible whenever the first was dismissed via Escape or the backdrop,
// because Bootstrap has hidden the element by then and only reacts to an
// `open` transition.
const active = ref<QueuedDialog>();
const isOpen = ref(false);

const promote = (): void => {
  if (active.value || !next.value) return;

  active.value = take();
  isOpen.value = true;
};

const close = (confirmed: boolean): void => {
  isOpen.value = false;
  // Resolving is idempotent, which matters: hiding the modal makes Bootstrap
  // emit its own `hide`, and that arrives back here as a second `cancel`.
  active.value?.resolve(confirmed);
};

const onClosed = (): void => {
  active.value = undefined;
  promote();
};

watch(next, promote);

onMounted(() => {
  registerHost();
  promote();
});

onUnmounted(() => {
  unregisterHost();
  active.value?.resolve(false);
});
</script>

<template>
  <BaseConfirmModal
    v-if="active"
    class="confirm-dialog-host-modal"
    :open="isOpen"
    :title="active.title"
    :confirm-text="active.confirmText"
    :cancel-text="active.cancelText"
    :confirm-kind="active.confirmKind"
    :confirm-icon="active.confirmIcon"
    :show-cancel-button="active.showCancelButton"
    @confirm="close(true)"
    @cancel="close(false)"
    @hidden="onClosed"
    @force-closed="onClosed"
  >
    <p class="mb-0">{{ active.text }}</p>
  </BaseConfirmModal>
</template>

<style lang="scss" scoped>
// Mounted in MainEntryPointApp, so this modal precedes any page-rendered modal
// in document order — at the shared `$zindex-modal` the page one would paint
// over it. Lift it, as `.swal2-container` did. `+ 1` stays free for a backdrop
// layer; this dialog's own backdrop is deliberately left at its normal height,
// because it is an opaque white blur (`_custom.scss`) that would erase the
// modal the dialog is asking about.
.confirm-dialog-host-modal {
  z-index: calc(#{$zindex-modal} + 2);
}
</style>
